import { Grid, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { DifferentAddressInput, DifferentAddressSchema, EditContractInput } from "../../validation/editContract";
import { Contract } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import { useGetCountries } from "@/services/RegisterCompany";
import { CSSTransition } from "react-transition-group";
import RadioButtonGroup from "@/components/molecules/RadioButtonGroup";
import TransportResponsibleEnum from "@/constants/enums/TransportResponsibleEnum";
import WeightResponsibleEnum from "@/constants/enums/WeightResponsibleEnu";
import Text from "@/components/atoms/Text";
import AddressTypeEnum from "@/constants/enums/AddressTypeEnum";
import ContractStatusEnum from "@/constants/enums/ContractStatusEnum";
import { useAuth } from "@/contexts/AuthContext";
import TextInput from "@/components/atoms/TextInput";
import CTA from "@/components/atoms/CTA";
import Select from "@/components/atoms/Select";
import Checkbox from "@/components/atoms/Checkbox";

interface TransportProps {
  contract: Contract;
  disableForm: boolean;
  editContractForm: UseFormReturn<EditContractInput>;
}

const Transport = ({ contract, disableForm, editContractForm }: TransportProps) => {
  const { t } = useTranslation();
  const differentAddressNodeRef = useRef(null);

  const { currentCompany, user, isViewer } = useAuth();

  const { data: countryOptions } = useGetCountries(user?.language.id as number, {
    staleTime: 1000 * 60 * 5,
    enabled: !!user?.language.id,
  });
  const [differentFromIsChecked, setDifferentFromIsChecked] = useState(false);
  const [differentToIsChecked, setDifferentToIsChecked] = useState(false);

  const transportResponsibleOptions = [
    { value: TransportResponsibleEnum.REVIVER, label: "Reviver" },
    { value: TransportResponsibleEnum.BUYER, label: t("orders:buyer") },
    { value: TransportResponsibleEnum.SELLER, label: t("orders:seller") },
  ];

  const weightReponsibleOptions = [
    { value: WeightResponsibleEnum.BUYER, label: t("orders:buyer") },
    { value: WeightResponsibleEnum.SELLER, label: t("orders:seller") },
  ];

  const {
    handleSubmit: handleSubmitAddress,
    control: addressControl,
    reset: resetAddress,
  } = useForm<DifferentAddressInput>({
    resolver: zodResolver(DifferentAddressSchema),
    defaultValues: {
      address: "",
      countryId: "",
      postCode: "",
      place: "",
      addressType: AddressTypeEnum.SHIPPING,
    },
    disabled: contract.contractStatus !== ContractStatusEnum.PENDING_APPROVAL,
  });

  function onSubmitAddress(data: any) {
    if (differentFromIsChecked) {
      editContractForm.setValue("transportFromAddress", data);
      setDifferentFromIsChecked(false);
      resetAddress();
    } else {
      editContractForm.setValue("transportToAddress", data);
      setDifferentToIsChecked(false);
      resetAddress();
    }
  }

  const fromAddress = () => {
    if (editContractForm.watch("transportFromAddress"))
      return `${editContractForm.watch("transportFromAddress.address")}, ${editContractForm.watch("transportFromAddress.postCode")},
          ${editContractForm.watch("transportFromAddress.place")},
          ${countryOptions?.find((coutry) => coutry.id === editContractForm.watch("transportFromAddress.country"))?.name}`;
    else
      return `${contract.transportFromAddress?.address}, ${contract.transportFromAddress?.postCode},
          ${contract.transportFromAddress?.place}, ${contract.transportFromAddress?.country.name}`;
  };

  const toAddress = () => {
    if (editContractForm.watch("transportToAddress"))
      return `${editContractForm.watch("transportToAddress.address")}, ${editContractForm.watch("transportToAddress.postCode")},
          ${editContractForm.watch("transportToAddress.place")},
          ${countryOptions?.find((country) => country.id === editContractForm.watch("transportToAddress.country"))?.name}`;
    else
      return `${contract.transportToAddress?.address}, ${contract.transportToAddress?.postCode},
          ${contract.transportToAddress?.place}, ${contract.transportToAddress?.country.name}`;
  };

  return (
    <>
      <Grid>
        <Grid.Col span={{ base: 12, xl: 6 }}>
          <Grid.Col span={12}>
            <Stack>
              <Text size="sm" color="secondary">
                {t("contracts:transportResponsible")}
              </Text>
              <Controller
                control={editContractForm.control}
                name="transportResponsible"
                render={({ field: { onChange, value } }) => (
                  <RadioButtonGroup
                    id={"edit-contract-transport-responsible"}
                    testId="edit-contract-transport-responsible"
                    name={"transportResponsible"}
                    disabled={disableForm}
                    options={transportResponsibleOptions}
                    defaultValue={value}
                    onValueChange={onChange}
                  />
                )}
              />
            </Stack>
          </Grid.Col>

          <Grid.Col span={12}>
            <Stack>
              <Stack>
                <Text size="xs" color="secondary">
                  {`${t("contracts:sellersAddress")} (${contract.contractSellers[0].companyName})`}
                </Text>{" "}
                {contract.transportFromAddress !== null && contract.transportFromAddress !== undefined && (
                  <Text size="lg" strong={true}>
                    {fromAddress()}
                  </Text>
                )}
              </Stack>
              {contract.createdByCompany === currentCompany?.companyId && (
                <Checkbox
                  id={"edit-contract-different-address"}
                  data-testid="edit-contract-different-address"
                  label={t("contracts:differentAddress")}
                  disabled={differentToIsChecked || isViewer(currentCompany)}
                  onChange={() => setDifferentFromIsChecked(!differentFromIsChecked)}
                />
              )}
            </Stack>
          </Grid.Col>
        </Grid.Col>
        <Grid.Col span={{ base: 12, xl: 6 }}>
          <Grid.Col span={12}>
            <Stack>
              <Text size="sm" color="secondary">
                {t("contracts:weightResponsible")}
              </Text>
              <Controller
                control={editContractForm.control}
                name="weightResponsible"
                render={({ field: { onChange, value } }) => (
                  <RadioButtonGroup
                    id={"edit-contract-weight-responsible"}
                    testId="edit-contract-weight-responsible"
                    name={"weightResponsible"}
                    disabled={disableForm}
                    options={weightReponsibleOptions}
                    defaultValue={value}
                    onValueChange={onChange}
                  />
                )}
              />
            </Stack>
          </Grid.Col>

          <Grid.Col span={12}>
            <Stack>
              <Stack>
                <Text size="xs" color="secondary">
                  {`${t("contracts:buyersAddress")} (${contract.contractBuyers[0].companyName})`}
                </Text>
                <Text size="lg" strong={true}>
                  {toAddress()}
                </Text>
              </Stack>
              {contract.createdByCompany === currentCompany?.companyId && (
                <Checkbox
                  id={"edit-contract-different-address"}
                  data-testid="edit-contract-different-address"
                  label={t("contracts:differentAddress")}
                  disabled={differentFromIsChecked || isViewer(currentCompany)}
                  onChange={() => setDifferentToIsChecked(!differentToIsChecked)}
                />
              )}
            </Stack>
          </Grid.Col>
        </Grid.Col>

        <CSSTransition
          in={differentFromIsChecked || differentToIsChecked}
          timeout={{
            enter: 1000,
            exit: 200,
          }}
          mountOnEnter
          unmountOnExit
          classNames="create-order-transitions"
          nodeRef={differentAddressNodeRef}
        >
          <Grid.Col span={12}>
            <div>
              <div ref={differentAddressNodeRef}>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Controller
                    control={addressControl}
                    name="address"
                    render={({ field, fieldState: { error } }) => (
                      <TextInput
                        id="create-order-address"
                        label={t("orders:orderAddress")}
                        {...field}
                        error={error ? t("validation:" + error.message) : null}
                      />
                    )}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Controller
                    control={addressControl}
                    name="countryId"
                    render={({ field: { onChange }, fieldState: { error } }) => (
                      <Select
                        id="add-address-country"
                        label={t("common:country")}
                        data={
                          countryOptions?.map((country) => ({
                            label: country.name,
                            value: country.id.toString(),
                          })) || []
                        }
                        onChange={onChange}
                        error={error ? t("validation:" + error.message) : null}
                      />
                    )}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Controller
                    control={addressControl}
                    name="postCode"
                    render={({ field, fieldState: { error } }) => (
                      <TextInput
                        id="create-order-postCode"
                        label={t("orders:postCode")}
                        {...field}
                        error={error ? t("validation:" + error.message) : null}
                      />
                    )}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Controller
                    control={addressControl}
                    name="place"
                    render={({ field, fieldState: { error } }) => (
                      <TextInput
                        id="create-order-place"
                        label={t("orders:place")}
                        {...field}
                        error={error ? t("validation:" + error.message) : null}
                      />
                    )}
                  />
                  <CTA id="checkAddress" onClick={handleSubmitAddress(onSubmitAddress)}>
                    {differentFromIsChecked ? "Lagre ny fra adresse" : "Lagre ny til adresse"}
                  </CTA>
                </Grid.Col>
              </div>
            </div>
          </Grid.Col>
        </CSSTransition>
      </Grid>
    </>
  );
};

export default Transport;
